import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAlert } from '../../../context/alert';
import { useTranslations } from '../../../hooks/useTranslations';
import { formatTime } from '../../../helpers/dates';
import { ScheduledNotificationResponse, CreateScheduledNotificationRequest, UpdateScheduledNotificationRequest } from '../../../types/api/notifications';
import { createScheduledNotification, updateScheduledNotification } from '../../../services/notifications';
import { fetchLanguages } from "../../../services/lang";
import ModalSheet from '../../basic/ModalSheet';
import LangModal from '../../categories/components/LangModal';
import { DateInput } from '../../basic/inputs/DateInput';
import { TimeInput } from '../../basic/inputs/TimeInput';
import { LanguageResponse } from '../../../types/api/lang';
import { Stack, Button, Textarea, Text, Spinner } from '@primer/react'

interface NotificationModalProps {
    notification: ScheduledNotificationResponse | undefined
    isOpen: boolean;
    onDidSaved: () => void;
    onClose: () => void;
}

export const NotificationModal: React.FC<NotificationModalProps> = ({ notification, isOpen, onDidSaved, onClose }) => {

    const [allLangs, setAllLangs] = useState<LanguageResponse[]>([]);
    const [isPending, setIsPending] = useState<boolean>(false)
    const [openLangModal, setLangModal] = React.useState(false)
    const [date, setDate] = useState<string>('');
    const [time, setTime] = useState<string>('');

    const {
        translations,
        setTranslations,
        langs,
        setLangs,
        currentLangIndex,
        setCurrentLangIndex,
        getCurrentFieldValue,
        updateFieldValue,
        isAllLanguagesFilled,
        langsDidUpdated
    } = useTranslations()

    const { setSuccess, setError, setInfo } = useAlert();

    const { mutate: fetchLanguagesRequest, isPending: isLoadingLang } = useMutation({
        mutationFn: fetchLanguages,
        onSuccess: (data) => {
            setAllLangs(data.list);
        },
        onError: (error: any) => {
            setAllLangs([]);
        }
    });

    const { mutate: createNotificationRequest } = useMutation({
        mutationFn: (data: CreateScheduledNotificationRequest) => createScheduledNotification(data),
        onSuccess: (data) => {
            setIsPending(false);
            onDidSaved()
            onClose()

            if (date.length === 0 || time.length === 0) {
                setSuccess('Уведомление отправлено')
            } else {
                setInfo('Уведомление запланировано')
            }
        },
        onError: (_) => setIsPending(false)
    });

    const { mutate: editNotificationRequest } = useMutation({
        mutationFn: (data: UpdateScheduledNotificationRequest) => updateScheduledNotification(notification?.id ?? '', data),
        onSuccess: (data) => {
            setIsPending(false);
            onDidSaved()
            onClose()

            if (date.length === 0 || time.length === 0) {
                setSuccess('Уведомление отправлено')
            } else {
                setInfo('Изменения сохранены')
            }
        },
        onError: (_) => setIsPending(false)
    });

    const createIsoString = (date: string, time: string): string => {
        const datePart = date.includes('T') ? date.split('T')[0] : date;
        return `${datePart}T${time}:00Z`;
    };

    const saveNotification = () => {
        const dateObj = new Date(time);
        const hours = dateObj.getUTCHours().toString().padStart(2, '0');
        const minutes = dateObj.getUTCMinutes().toString().padStart(2, '0');
        const timeStr = `${hours}:${minutes}`;

        let dateTime: string | undefined = createIsoString(date, time)

        if (date.length === 0 || time.length === 0) {
            dateTime = undefined
        }

        setIsPending(true)
        createNotificationRequest({
            plannedDt: dateTime,
            userIds: [],
            translations: translations
        })
    }

    const updateNotification = () => {
        let dateTime: string | undefined = createIsoString(date, time)

        if (date.length === 0 || time.length === 0) {
            dateTime = undefined
        }

        setIsPending(true)
        editNotificationRequest({
            plannedDt: dateTime,
            userIds: [],
            translations: translations
        })
    }

    const trySaveNotification = () => {
        if (translations.length === 0) {
            setError('Уведомления заполнены не для всех выбранных языков')
            return
        }
        if (!isAllLanguagesFilled('title') || !isAllLanguagesFilled('body')) {
            setError('Уведомления заполнены не для всех выбранных языков')
            return
        }

        if (
            (date.length > 0 && time.length === 0) ||
            (date.length === 0 && time.length > 0)
        ) {
            setError('Заполните дату и время или очистите для отправки уведомления сразу')
            return;
        }

        if (notification) {
            // Update
            updateNotification()
        } else {
            // Create
            saveNotification()
        }
    };

    useEffect(() => {
        setTranslations(notification?.translations ?? [])
        fetchLanguagesRequest({})

        const dateStr = notification?.plannedDt
        setDate(dateStr ?? "")

        if (dateStr) {
            const timeStr = formatTime(dateStr)
            setTime(timeStr)
        }
    }, [notification]);

    useEffect(() => {
        if (notification && allLangs) {
            // Если есть translations, используем их
            if (notification.translations && notification.translations.length > 0) {
                const notificationLangCodes = notification.translations.map(el => el.languageIsoCode);
                const selectedLangs = allLangs.filter(lang => notificationLangCodes.includes(lang.isoCode));
                setLangs(selectedLangs);
                setTranslations(notification.translations);
            }
        } else if (allLangs) {
            const russianLang = allLangs.find(lang => lang.isoCode === 'ru');
            if (russianLang) {
                setLangs([russianLang]);
                setTranslations([{
                    languageIsoCode: 'ru',
                    fields: [{ fieldName: 'title', value: "" }]
                }]);
                setCurrentLangIndex(0);
            }
        }
    }, [allLangs, notification]);

    const title = notification === undefined ? 'Создать уведомление' : 'Редактировать уведомление'
    const saveButtonTitle = notification === undefined ? 'Создать' : 'Сохранить изменения'

    return (
        <ModalSheet
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            size="middle"
            headerSize="regular"
        >
            {({ setTitle }) => (
                <Stack className='mt-[16px]'>
                    <Stack direction="horizontal" gap='condensed' justify='start'>
                        {langs.map((lang, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentLangIndex(index)}
                                className={`flex items-center gap-[4px] px-2 py-[6px] border-[1px] 
                        ${currentLangIndex === index ? "bg-aloix-black hover:bg-gray-800" : "bg-white hover:bg-gray-50 border-border"}
                         rounded-md  h-8`}>
                                <span className={`${currentLangIndex === index ? "text-white" : "text-aloix-black"} font-medium text-xs`}>{lang.titleRu || lang.titleOrigin}</span>
                            </button>
                        ))}
                        <LangModal
                            filterOpened={openLangModal}
                            setFilterOpened={(val) => setLangModal(val)}
                            selectedLangs={langs}
                            setSelectedFilter={langsDidUpdated}
                            onApply={() => { }}
                            onDismiss={() => { }}
                        />
                    </Stack>
                    {isLoadingLang && <Spinner />}
                    {langs.length > 0 &&
                        <>
                            <Text as="span" weight='semibold' className='text-[14px] text-aloix-black'>
                                Заголовок
                            </Text>
                            <Textarea
                                inputMode="text"
                                value={getCurrentFieldValue('title')}
                                onChange={(e) => updateFieldValue('title', e.target.value)}
                                maxLength={100}
                                rows={3}
                                resize="none"
                                placeholder="Напишите заголовок уведомления"
                                contrast={true}
                            />

                            <Text as="span" weight='semibold' className='text-[14px] text-aloix-black'>
                                Тело
                            </Text>
                            <Textarea
                                inputMode="text"
                                value={getCurrentFieldValue('body')}
                                onChange={(e) => updateFieldValue('body', e.target.value)}
                                maxLength={1024}
                                rows={6}
                                resize="none"
                                placeholder="Напишите тело уведомления"
                                contrast={true}
                            />
                        </>
                    }

                    <Text as="span" weight='semibold' className='text-[14px] text-aloix-black'>
                        Дата и время отправки
                    </Text>
                    <Stack direction="horizontal" gap='normal' justify='center'>
                        <DateInput
                            value={date}
                            onChange={setDate}
                            placeholder='Дата отправки'
                            type="from"
                            minDate={new Date().toISOString().split('T')[0]}
                            className='w-full !h-[40px]'
                        />
                        <TimeInput
                            value={time}
                            onChange={setTime}
                            placeholder='Время отправки'
                            date={date}
                            className='w-full !h-[40px]'
                        />
                    </Stack>
                    <Stack direction="horizontal" gap='normal' justify='end'>
                        <div></div>
                        <Button
                            onClick={onClose}
                            size='small'
                            variant="default">
                            Отмена
                        </Button>
                        <Button
                            onClick={trySaveNotification}
                            size='small'
                            variant="primary"
                            loading={isPending}
                            disabled={isPending || translations.length === 0}>
                            {saveButtonTitle}
                        </Button>
                    </Stack>
                </Stack>
            )}
        </ModalSheet>
    );
};